// Tenant home — the post-sign-up experience. Loads /api/me; if the user has no company yet it shows
// onboarding (create your first company: name → country → entity type), otherwise their companies.
// Distinct from the admin console; a tenant bearer authenticates the /api/me* calls.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { currentUser, logout } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';
import { bottomNav, tenantNavItems } from '../shared/bottomnav.js';
import { t } from '../shared/i18n.js';
import { demoBadge, isDemoCompany, DEMO_ROW_NOTE } from '../shared/demo.js';
import { startPayfastCheckout, billingRowState } from './billing.js';
import { rememberCompany, takeReturn, clearReturn } from './tshell.js';
import { versionBadge, releaseNotesModal } from '../shared/version.js';
import { searchSelect } from '../shared/searchselect.js';

export function tenantHomeScreen(){
  document.body.classList.remove('vt');   // the company picker is aboo-skinned (vt.css is tenant-only)
  document.title = 'aboo';                 // company screens set "<Company> · aboo"; the picker is neutral
  const me       = signal(null);          // { user, companies }
  const loading  = signal(true);
  const err      = signal('');
  const signingOut = signal(false);
  const activating = signal(0), activateErr = signal('');   // PayFast checkout hand-off
  // Switch-company "undo": if we arrived here from the 🏢 pill, offer a one-click return to the exact
  // company + screen we left (owner 2026-09-15 — "no back button... in case you pressed it by mistake").
  const backTo = signal(takeReturn());
  function returnToCompany(){ const r = backTo.value; if(!r) return; clearReturn(); backTo.value = null; navigate(r.path || ('/co/' + r.id + '/bank')); }

  // Every registration owns one automatic demo company. It is resettable and deletable; the API
  // self-heals a missing demo on the next /me load. Reset and delete both destroy data, so both are
  // confirm-gated in place: `demoConfirm` holds 'reset:<id>' / 'delete:<id>' while one is armed.
  const demoBusy = signal(false), demoErr = signal(''), demoConfirm = signal('');

  // onboarding / add-company form
  const formOpen = signal(false);
  const fName = signal(''), fCountry = signal(''), fEntity = signal(''), fPayer = signal('practice');
  const saving = signal(false), formErr = signal('');
  const noEntities = signal(false);   // a country was picked but aboo has no entity types for it yet
  // The accounting standard (owner 2026-10-21): the country's by default, and IFRS / GAAP to choose where the
  // country list says a new company may take GAAP there. stdCountry is null where there is no choice to make — then
  // nothing is sent and the API gives the country's standard, exactly as before.
  const fStd = signal(''), stdCountry = signal(null);
  const countryRows = new Map();
  const isAcct = () => !!(me.value && me.value.user && me.value.user.signup_kind === 'accountant');
  const noun = () => isAcct() ? 'client' : 'company';   // accountants manage clients
  // Clear the "required" banner the moment the user starts fixing a field, instead of leaving it up
  // until the next submit. Mirrors the invoice editor's per-field clearInvalid (see CLAUDE.md).
  const clearFormErr = () => { if(formErr.value) formErr.value = ''; };

  // Country / entity are iT searchSelects (S14 dropdown parity) built ONCE per screen and MOVED
  // into each form() re-render by node interpolation — nothing the form re-renders on is written
  // while typing in them, so they can't lose focus mid-keystroke.
  // className form-control: this screen is aboo-skinned (no body.vt), so the inputs take the aboo
  // field look; the popup gets its base skin from aboo.css's .ss-list block.
  const countrySel = searchSelect({
    placeholder: '— Select a country —', ariaLabel: 'Country', className: 'form-control',
    onSelect: v => { clearFormErr(); fCountry.value = v; fEntity.value = ''; entitySel.set(''); pickStandard(v); loadEntities(v); },
  });
  const entitySel = searchSelect({
    placeholder: '— Select an entity type —', ariaLabel: 'Entity type', className: 'form-control',
    emptyText: 'Pick a country first',
    onSelect: v => { clearFormErr(); fEntity.value = v; },
  });

  async function loadMe(){
    loading.value = true;
    try { me.value = await api.get('/me'); }
    catch(e){
      if(e.status === 401){ logout(); navigate('/login'); return; }
      err.value = 'Could not load your account';
    }
    finally { loading.value = false; }   // always clear — never leave the screen stuck on "Loading…"
  }
  loadMe();
  (async () => {
    try {
      const rows = (await api.get('/admin/countries?per_page=200')).data || [];
      rows.forEach(c => countryRows.set(String(c.id), c));
      countrySel.setOptions(rows.map(c => ({ value: String(c.id), label: c.name || '' })));
    } catch {}
  })();

  // A newly picked country brings its own default; the choice shows only where GAAP may be chosen there
  function pickStandard(cid){
    const c = countryRows.get(String(cid));
    stdCountry.value = (c && c.gaap_available) ? { name: c.name || '', standard: c.accounting_standard || 'IFRS' } : null;
    fStd.value = stdCountry.value ? stdCountry.value.standard : '';
  }

  async function loadEntities(cid){
    entitySel.setOptions([]);
    noEntities.value = false;
    if(!cid){ entitySel.setEmptyText('Pick a country first'); return; }
    try {
      const rows = (await api.get('/admin/countries/' + cid + '/entity-types?per_page=200')).data || [];
      entitySel.setOptions(rows.map(x => ({ value: String(x.id), label: x.name || '' })));
      // A country with no entity types can't be onboarded (the type seeds the chart). Say so
      // plainly — the misleading "Pick a country first" made it look like nothing was chosen.
      const empty = rows.length === 0;
      noEntities.value = empty;
      entitySel.setEmptyText(empty ? 'No entity types for this country yet' : 'Pick a country first');
    } catch { entitySel.setOptions([]); }
  }
  function openForm(){
    fName.value = ''; fCountry.value = ''; fEntity.value = ''; fPayer.value = 'practice';
    fStd.value = ''; stdCountry.value = null;
    countrySel.set(''); entitySel.set(''); entitySel.setOptions([]); entitySel.setEmptyText('Pick a country first');
    formErr.value = ''; noEntities.value = false; formOpen.value = true;
  }
  function closeForm(){ formOpen.value = false; }
  async function submit(e){
    e.preventDefault();
    if(!fName.value.trim() || !fCountry.value || !fEntity.value){ formErr.value = (isAcct() ? 'Client business name' : 'Company name') + ', country and entity type are required.'; return; }
    saving.value = true; formErr.value = '';
    try {
      const body = { name: fName.value.trim(),
        country_id: Number(fCountry.value), entity_type_id: Number(fEntity.value),
        license_payer: isAcct() ? fPayer.value : 'business' };
      if(stdCountry.value) body.accounting_standard = fStd.value;
      await api.post('/me/companies', body);
      formOpen.value = false; await loadMe();
    } catch(e2){
      // Never surface a raw DB/stack error to the user (GitHub #51 showed a check-constraint dump).
      // Prefer the server's message, but fall back to a friendly line if it reads like logs.
      const raw = (e2.data && e2.data.error) || '';
      const looksLikeLogs = raw.length > 160 || /\b(select|insert|update|constraint|violat|relation|traceback|exception|null)\b/i.test(raw);
      formErr.value = (raw && !looksLikeLogs) ? raw : ('Could not create the ' + noun() + ' — please try again.');
    }
    saving.value = false;
  }
  function signOut(){ logout(); navigate('/login'); }

  async function activate(c){
    activateErr.value = ''; activating.value = c.id;
    try { await startPayfastCheckout(c.id); }         // full-page redirect to PayFast on success
    catch(e){ activateErr.value = (e.data && e.data.error) || e.message || 'Could not start checkout'; activating.value = 0; }
  }
  // Billing state on a company row (decision in billingRowState): active → green pill; a business-pays
  // client is activated by the business (not the accountant); otherwise an "Activate billing" button.
  const billingAction = (c) => {
    const st = billingRowState(c, isAcct());
    if(st.kind === 'active') return html`<span class="row-status ok">Billing active</span>`;
    if(st.kind === 'invite') return html`<span class="row-sub">Client to activate</span>`;
    // the price is in the plan's (tier's) own currency — its mark when /me sends it, else the
    // company's (the plan is priced in the company's country currency)
    const price = st.price ? ` · ${c.tier_currency_symbol || c.base_currency_symbol || ''}${st.price}/mo` : '';
    return html`<button class="btn btn-sm btn-primary" ?disabled=${() => activating.value === c.id}
        @click=${() => activate(c)}>${() => activating.value === c.id ? 'Starting…' : 'Activate billing' + price}</button>`;
  };

  // Open a company's books: the card, and the "›" at the end of its actions. The "›" sits INSIDE
  // .row-actions, whose stopPropagation keeps the buttons from also opening the company — so the
  // "›" needs this handler of its own, or it does nothing (dead since 2026-08-18; owner 2026-10-19).
  const openCompany = c => { clearReturn(); rememberCompany(c.id, c.name); navigate('/co/' + c.id + '/bank'); };

  async function demoAction(run){
    demoBusy.value = true; demoErr.value = ''; demoConfirm.value = '';
    try { await run(); await loadMe(); }
    catch(e){ demoErr.value = (e.data && e.data.error) || e.message || 'That did not work'; }
    demoBusy.value = false;
  }
  const resetDemo  = c => demoAction(() => api.post('/companies/' + c.id + '/demo/reset', {}));
  const deleteDemo = c => demoAction(() => api.del('/companies/' + c.id + '/demo'));

  // Reset and delete both destroy data, so neither is one tap. Same inline pattern as sign-out.
  const demoActions = (c) => {
    const armed = demoConfirm.value;
    if(armed === 'reset:' + c.id) return html`<span class="confirm-inline">
      <span class="confirm-q">Reset — wipes this practice data?</span>
      <button type="button" class="btn-confirm-yes" ?disabled=${() => demoBusy.value} @click=${() => resetDemo(c)}>${t('setup.yes', 'Yes')}</button>
      <button type="button" class="btn-confirm-no" @click=${() => { demoConfirm.value = ''; }}>${t('setup.no', 'No')}</button></span>`;
    if(armed === 'delete:' + c.id) return html`<span class="confirm-inline">
      <span class="confirm-q">Delete the demo company?</span>
      <button type="button" class="btn-confirm-yes" ?disabled=${() => demoBusy.value} @click=${() => deleteDemo(c)}>${t('setup.yes', 'Yes')}</button>
      <button type="button" class="btn-confirm-no" @click=${() => { demoConfirm.value = ''; }}>${t('setup.no', 'No')}</button></span>`;
    return html`
      <button class="btn btn-sm btn-outline" title="Wipe the practice data and start from the seeded baseline"
              @click=${() => { demoConfirm.value = 'reset:' + c.id; }}>Reset</button>
      <button class="btn btn-sm btn-outline" title="Delete the demo company — you can create a new one afterwards"
              @click=${() => { demoConfirm.value = 'delete:' + c.id; }}>${t('common.delete', 'Delete')}</button>`;
  };

  // Import is offered only once billing is ACTIVE. The agreed flow is sign-up → create the company
  // (always fresh, seeded chart) → activate billing → THEN bring data across, so showing "Import
  // data" on a company that is still trialing contradicts it and invites the user down a path we
  // would rather they took after paying. Reuses billingRowState so there is one source of truth
  // for "is this company activated", not a second copy that can drift.
  // ...with ONE exception: the demo company. Practising an import is the whole reason it exists,
  // and it is never billed, so gating its import on an active subscription would gate the feature
  // on something that can never happen.
  // The chart is the SETUP CONFIRMATION — "your company is set up" was previously a claim the user
  // could not check. So unlike the import it is offered on EVERY company, trialing or not and demo
  // or not: seeing the accounts you were given is never something you pay for.
  const chartAction = (c) => html`
    <button class="btn btn-sm btn-outline" title="See the accounts this company was set up with"
            @click=${() => navigate('/companies/' + c.id + '/accounts')}>Chart of accounts</button>`;

  // Pop a company out into its OWN browser window (owner 2026-09-24) — two sets of books side by side.
  // The window is NAMED per company, so a second click brings that window to the front instead of
  // opening a duplicate. Login carries over: localStorage when "Remember me", otherwise the browser
  // copies this tab's sessionStorage into the new window (which is why this must NOT use `noopener`).
  // Every per-company cache is keyed by company id and the company lives in the URL, so two companies
  // in two windows never clash. Refresh tokens aren't single-use (Keycloak default), so each window
  // refreshes on its own without signing the others out.
  function popOut(c){
    rememberCompany(c.id, c.name);
    const w = window.open('/co/' + c.id + '/bank', 'aboo-co-' + c.id, 'popup,width=1280,height=900');
    if(w) w.focus();
  }
  const popOutAction = (c) => html`
    <button class="btn btn-sm btn-outline co-popout" title=${'Open ' + c.name + ' in its own window'}
            aria-label=${'Open ' + c.name + ' in a new window'}
            @click=${() => popOut(c)}>${icon('external-link', {size: 14})} ${t('home.new_window', 'New window')}</button>`;

  const importAction = (c) =>
    (isDemoCompany(c) || billingRowState(c, isAcct()).kind === 'active')
      ? html`<button class="btn btn-sm btn-outline" title="Bring a chart of accounts across from another system"
                     @click=${() => navigate('/companies/' + c.id + '/import')}>Import data</button>`
      : null;

  const payerBtn = (val, label, sub) => html`
    <button type="button" class=${'signup-kind' + (fPayer.value === val ? ' active' : '')} @click=${() => { fPayer.value = val; }}>
      <span class="signup-kind-label">${label}</span><span class="signup-kind-sub">${sub}</span>
    </button>`;

  // The standard, as buttons like the licence choice. One reactive slot that reads its signals first and renders
  // plain values inside (a nested slot here would append on re-render — CLAUDE.md gotchas).
  const standardChoice = () => {
    const c = stdCountry.value, chosen = fStd.value;
    if(!c) return '';
    const btn = (val, sub) => html`
      <button type="button" class=${'signup-kind' + (chosen === val ? ' active' : '')} @click=${() => { fStd.value = val; }}>
        <span class="signup-kind-label">${val}</span><span class="signup-kind-sub">${sub + (c.standard === val ? ' · default' : '')}</span>
      </button>`;
    return html`
      <label class="form-label mt-2">Accounting standard</label>
      <div class="signup-kinds std-kinds mb-2">
        ${btn('IFRS', 'International standards')}
        ${btn('GAAP', c.name + '\'s own rules')}
      </div>
      <div class="form-note">${c.name}'s default is ${c.standard}. ${c.standard === 'GAAP'
        ? `Choose IFRS only if your ${noun()} must report under it — a listed company does, for example.`
        : `Choose GAAP only if your ${noun()} must report under ${c.name}'s own rules.`} This can't be changed after the ${noun()} is created.</div>`;
  };

  const form = () => {
    const has = me.value && me.value.companies.length;
    const acct = isAcct();
    return html`
    <form class="login-card card onboard-card" @submit=${submit}>
      <div class="card-body">
        <h2 class="onboard-title">${has ? (acct ? 'Add a client' : 'Add a company') : (acct ? 'Add your first client' : t('common.welcome_setup', 'Set up your first company'))}</h2>
        <p class="login-hint mb-3">${acct ? 'A fresh set of books for your client — we\'ll seed the chart of accounts from the entity type.' : 'A fresh set of books — we\'ll seed the chart of accounts from the entity type.'}</p>
        <label class="form-label">${acct ? 'Client business name' : t('setup.company_name', 'Company name')}</label>
        <input class="form-control mb-2" .value=${fName} @input=${e => { clearFormErr(); fName.value = e.target.value; }} placeholder="e.g. Acme Trading (Pty) Ltd">
        <label class="form-label">Country</label>
        <div class="mb-2">${countrySel.el}</div>
        <label class="form-label">Entity type</label>
        <div class="mb-2">${entitySel.el}</div>
        ${() => noEntities.value
          ? html`<div class="alert-warn mb-2">aboo doesn't have entity types set up for this country yet, so a ${noun()} can't be created here. Please choose a different country.</div>`
          : html`<div class="form-note">The entity type's default ledger codes seed this ${noun()}'s chart.</div>`}
        ${standardChoice}
        ${acct ? html`
          <label class="form-label mt-2">Who pays the licence?</label>
          <div class="signup-kinds mb-2">
            ${() => payerBtn('practice', 'I\'ll pay', 'On my practice account')}
            ${() => payerBtn('business', 'The business pays', 'Flag it to the client')}
          </div>` : ''}
        ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr}</div>` : null}
        <div class="form-actions">
          ${() => (me.value && me.value.companies.length) ? html`<button type="button" class="btn btn-outline" @click=${closeForm}>${t('common.cancel', 'Cancel')}</button>` : ''}
          <span class="form-actions-spacer"></span>
          <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Creating…' : (acct ? 'Create client' : t('setup.create_company', 'Create company'))}</button>
        </div>
      </div>
    </form>`;
  };

  const list = () => {
    const cos = me.value.companies;
    const acct = isAcct();
    // Show the return-to-company control only if we came from the pill AND that company is still ours.
    const r = backTo.value;
    const canReturn = r && cos.some(c => String(c.id) === String(r.id));
    return html`<div class="page onboard-page">
      ${canReturn ? html`<div class="co-backbar">
        <button class="btn btn-outline" @click=${returnToCompany}>← ${t('common.back_to', 'Back to')} ${r.name || t('common.company', 'the company')}</button>
      </div>` : ''}
      <div class="page-head with-action">
        <div><h1 class="page-title">${acct ? 'Your clients' : t('common.your_companies', 'Your companies')}</h1><div class="page-sub">${cos.length} ${acct ? (cos.length === 1 ? 'client' : 'clients') : (cos.length === 1 ? 'company' : 'companies')}</div></div>
        <button class="btn btn-primary" @click=${openForm}>+ Add ${acct ? 'client' : 'company'}</button>
      </div>
      ${() => activateErr.value ? html`<div class="alert alert-danger py-2 mb-2">${activateErr}</div>` : null}
      ${() => demoErr.value ? html`<div class="alert alert-danger py-2 mb-2">${demoErr}</div>` : null}
      <div class="row-list">${cos.map(c => isDemoCompany(c)
        // The demo row: badged, resettable, deletable — and NO billing action, because it is
        // excluded from usage, tiering and any company limit. Offering "Activate billing" on a
        // company that is never billed would be a lie the server would then refuse.
        ? html`
        <div class="row-card co-card" @click=${() => openCompany(c)}>
          <span class="row-main"><b>${c.name}</b>${demoBadge()}<span class="row-sub">${DEMO_ROW_NOTE}</span></span>
          <span class="row-actions" @click=${e => e.stopPropagation()}>${chartAction(c)}${importAction(c)}${() => demoActions(c)}${popOutAction(c)}<span class="row-open" @click=${() => openCompany(c)}>${icon('chevron-right', {size: 14})}</span></span>
        </div>`
        : html`
        <div class="row-card co-card" @click=${() => openCompany(c)}>
          <span class="row-main"><b>${c.name}</b><span class="row-sub">${c.role_name || c.role_code || 'member'}${c.tier_name ? ' · ' + c.tier_name : ''}${c.license_payer === 'practice' ? ' · you pay' : (acct ? ' · client pays' : '')}${c.status && c.status !== 'active' ? ' · ' + c.status : ''}</span></span>
          <span class="row-actions" @click=${e => e.stopPropagation()}>${chartAction(c)}${importAction(c)}${billingAction(c)}${popOutAction(c)}<span class="row-open" @click=${() => openCompany(c)}>${icon('chevron-right', {size: 14})}</span></span>
        </div>`)}</div>
      <div class="hint">Open a ${acct ? 'client' : 'company'} to work in its books — Bank accounts are live.</div>
      <div class="home-version">${versionBadge('long')}</div>
    </div>`;
  };

  const body = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    if(!me.value) return html`<div class="muted-block">Loading…</div>`;
    if(formOpen.value || !me.value.companies.length) return html`<div class="onboard-wrap">${form()}</div>`;
    return list();
  };

  return html`
    <div class="main tenant-main">
      <header class="app-bar">
        <div class="brand-lockup"><span class="brand-dot"></span><span class="brand-name">aboo</span></div>
        <div class="app-title">Home</div>
        <div class="app-user">
          <button class="theme-toggle" title="Toggle light / dark" @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
          <span class="user-name">${currentUser}</span>
          ${() => signingOut.value
            ? html`<span class="confirm-inline"><span class="confirm-q">Sign out?</span>
                <button type="button" class="btn-confirm-yes" @click=${() => { signingOut.value = false; signOut(); }}>${t('setup.yes', 'Yes')}</button>
                <button type="button" class="btn-confirm-no" @click=${() => { signingOut.value = false; }}>${t('setup.no', 'No')}</button></span>`
            : html`<button class="btn btn-sm btn-outline" @click=${() => { signingOut.value = true; }}>Sign out</button>`}
        </div>
      </header>
      <div class="content">${body}</div>
      ${bottomNav(tenantNavItems({ onSignOut: signOut }), 'home')}
      ${releaseNotesModal()}
    </div>`;
}
